import { PAGES } from "@/constants/pagesNames";
import {
  SELECTED_GROUP_URL_KEY,
  SELECTED_PIPELINE_URL_KEY,
} from "@/constants/url";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

export function useSearchParams() {
  const route = useRoute();
  const router = useRouter();

  const queryParamsObject = computed(() => {
    let params = route.query;
    if (!Object.keys(params).length)
      params = Object.fromEntries(new URLSearchParams(window.location.search));

    return params;
  });

  const queryParamsString = computed(() => route.fullPath.includes("?") ? route.fullPath.split("?").pop() : '');

  function useSearchParamByKey(
    key: string,
    pagePathToRedirectWhenRemoveSearchParam?: string
  ) {
    const dataBySearchParamKey = computed(
      () => queryParamsObject.value[key] as string
    );

    async function setSearchParam(paramValue: string, shouldReplace?: boolean) {
      const routerAction = shouldReplace ? router.replace : router.push;
      await routerAction({
        path: window.location.pathname,
        query: { ...queryParamsObject.value, [key]: paramValue },
      });
    }

    async function removeSearchParam(
      shouldRedirectToOtherPage?: boolean,
      pagePath = pagePathToRedirectWhenRemoveSearchParam
    ) {
      if (key in route.query) {
        const newQuery = structuredClone(queryParamsObject.value);
        delete newQuery[key];

        await router.replace({
          path: shouldRedirectToOtherPage ? pagePath : window.location.pathname,
          query: newQuery,
        });
      }
    }

    return {
      dataBySearchParamKey,
      setSearchParam,
      removeSearchParam,
    };
  }

  return {
    queryParamsObject,
    queryParamsString,
    useSearchParamByKey,
  };
}

export function useSelectedPipelineIdInURL() {
  const { useSearchParamByKey } = useSearchParams();
  const { dataBySearchParamKey, setSearchParam, removeSearchParam } =
    useSearchParamByKey(SELECTED_PIPELINE_URL_KEY, PAGES.DASHBOARD.PATH);

  return {
    pipelineIdInURL: dataBySearchParamKey,
    setPipelineIdToURL: setSearchParam,
    removePipelineIdFromURL: removeSearchParam,
  };
}

export function useSelectedGroupInURL() {
  const { useSearchParamByKey } = useSearchParams();
  const { dataBySearchParamKey, setSearchParam, removeSearchParam } =
    useSearchParamByKey(SELECTED_GROUP_URL_KEY);

  return {
    groupInURL: dataBySearchParamKey,
    setGroupToURL: setSearchParam,
    removeGroupFromURL: removeSearchParam,
  };
}
